import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@/shared/ui/Icon'
import { cn } from '@/shared/utils/cn'
import { useCopyToClipboard } from '@/shared/utils/useCopyToClipboard'

type FeedbackKind = 'up' | 'down' | null

interface Props {
  timestampLabel: string
  sourceCount: number
  responseSeconds?: number | null
  /** Text to copy when the copy button is pressed. */
  copyText?: string
  /** Stable id used to key feedback state. */
  messageId?: string
}

/**
 * 7.8I — actionable message footer.
 *
 * - **Copy** uses the shared {@link useCopyToClipboard} hook (async Clipboard
 *   API with a hidden-textarea fallback for non-secure contexts), then briefly
 *   swaps the button into a confirmation state.
 * - **Thumb up/down** are local-only today (no backend feedback endpoint in
 *   step 7.8I). State persists in memory for the lifetime of the page so the
 *   button stays visibly toggled until the user un-toggles or refreshes.
 */
export function MessageActions({
  timestampLabel,
  sourceCount,
  responseSeconds,
  copyText,
  messageId,
}: Props) {
  const { copied, copy } = useCopyToClipboard()
  const [feedback, setFeedback] = useState<FeedbackKind>(null)

  useEffect(() => {
    setFeedback(null)
  }, [messageId])

  const handleCopy = useCallback(() => {
    if (!copyText) return
    void copy(copyText)
  }, [copyText, copy])

  const toggleFeedback = useCallback((kind: Exclude<FeedbackKind, null>) => {
    setFeedback((prev) => (prev === kind ? null : kind))
  }, [])

  const timingLabel =
    typeof responseSeconds === 'number' && responseSeconds > 0
      ? ` · ${responseSeconds.toFixed(1)}s`
      : ''

  return (
    <div className="msg-tools">
      <button
        type="button"
        title={copied ? 'Copiado' : 'Copiar resposta'}
        aria-label={copied ? 'Copiado' : 'Copiar resposta'}
        className={cn({ copied })}
        onClick={(e) => {
          e.stopPropagation()
          handleCopy()
        }}
        disabled={!copyText}
      >
        <Icon name={copied ? 'check' : 'copy'} />
      </button>
      <button
        type="button"
        title="Útil"
        aria-label="Marcar como útil"
        aria-pressed={feedback === 'up'}
        className={cn({ 'is-active': feedback === 'up' })}
        onClick={(e) => {
          e.stopPropagation()
          toggleFeedback('up')
        }}
      >
        <Icon name="thumb-up" />
      </button>
      <button
        type="button"
        title="Não útil"
        aria-label="Marcar como não útil"
        aria-pressed={feedback === 'down'}
        className={cn('thumb-down', { 'is-active': feedback === 'down' })}
        onClick={(e) => {
          e.stopPropagation()
          toggleFeedback('down')
        }}
      >
        <Icon name="thumb-down" />
      </button>
      <span className="sep" aria-hidden="true" />
      <span className="ts">
        {timestampLabel} · {sourceCount} fontes{timingLabel}
      </span>
    </div>
  )
}
